import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Form, Input, Select, DatePicker, message, Row, Col, Statistic, Divider, Descriptions, Timeline, Alert, Tooltip, InputNumber } from 'antd';
import { SearchOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

  const STATUS_TEXT = {
    pending: '待审核',
    processing: '处理中',
    paid: '通过',
    failed: '失败',
    rejected: '已拒绝'
  };

const ThirdPartyWithdrawals = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_amount: 0, page: 1, limit: 20 });
  const [filters, setFilters] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('detail');
  const [detailData, setDetailData] = useState(null);
  const [auditForm] = Form.useForm();
  const [appList, setAppList] = useState([]);
  const [changeVisible, setChangeVisible] = useState(false);
  const [changeRecord, setChangeRecord] = useState(null);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeForm] = Form.useForm();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const changeTargetStatus = Form.useWatch('target_status', changeForm);

  useEffect(() => {
    loadData();
    loadAppList();
  }, [pagination.page]);

  const loadAppList = async () => {
    try {
      const response = await api.get('/admin/third-party/apps', { params: { status: 1, limit: 100 } });
      if (response.code === 200) {
        setAppList(response.data.list || []);
      }
    } catch (error) {
      console.error('加载应用列表失败', error);
    }
  };

  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/third-party-withdrawals', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          ...filters,
          ...params
        }
      });

      if (response.code === 200) {
        setDataSource(response.data.list || []);
        setPagination({
          ...pagination,
          total: response.data.pagination.total,
          total_amount: response.data.pagination.total_amount,
          page: response.data.pagination.page
        });
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
    loadData({ ...newFilters, page: 1 });
  };

  const handleReset = () => {
    setFilters({});
    setPagination({ ...pagination, page: 1 });
    loadData({ page: 1 });
  };

  const handleViewDetail = async (record) => {
    try {
      setHistoryList([]);
      setHistoryLoading(true);
      const [detailResponse, historyResponse] = await Promise.all([
        api.get(`/admin/third-party-withdrawals/${record.id}`),
        api.get(`/admin/third-party-withdrawals/${record.withdraw_no}/history`)
      ]);
      if (detailResponse.code === 200) {
        setDetailData(detailResponse.data);
      }
      setHistoryList(historyResponse.data || []);
      setModalMode('detail');
      setModalVisible(true);
    } catch (error) {
      message.error('获取详情失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAudit = async (record) => {
    try {
      const response = await api.get(`/admin/third-party-withdrawals/${record.id}`);
      if (response.code === 200) {
        setDetailData(response.data);
        const amount = parseFloat(response.data.amount || 0);
        const exchangeRate = parseFloat(response.data.exchange_rate) || 1;
        const feeRate = parseFloat(response.data.fee_rate) || 0;
        auditForm.setFieldsValue({
          audit_status: 'approved',
          audit_remark: '',
          amount: amount,
          cny_amount: amount / exchangeRate,
          fee_amount: amount * feeRate,
          exchange_rate: exchangeRate,
          fee_rate: feeRate
        });
        setModalMode('audit');
        setModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleAuditSubmit = async () => {
    try {
      const values = await auditForm.validateFields();
      const payload = {
        audit_status: values.audit_status,
        audit_remark: values.audit_remark,
        amount: values.amount
      };
      const response = await api.put(`/admin/third-party-withdrawals/${detailData.id}/audit`, payload);
      if (response.code === 200) {
        message.success(response.message || '审核成功');
        setModalVisible(false);
        loadData();
      }
    } catch (error) {
      message.error(error.message || '审核失败');
    }
  };

  const handleChange = async (record) => {
    try {
      const response = await api.get(`/admin/third-party-withdrawals/${record.id}`);
      if (response.code === 200) {
        setChangeRecord(response.data);
        setChangeVisible(true);
        const currentStatus = response.data.status;
        let defaultTarget = 'failed';
        if (currentStatus === 'failed' || currentStatus === 'rejected') {
          defaultTarget = 'paid';
        }
        const amount = parseFloat(response.data.amount || 0);
        const exchangeRate = parseFloat(response.data.exchange_rate) || 1;
        const feeRate = parseFloat(response.data.fee_rate) || 0;
        changeForm.setFieldsValue({
          target_status: defaultTarget,
          amount: amount,
          cny_amount: amount / exchangeRate,
          fee_amount: amount * feeRate,
          exchange_rate: exchangeRate,
          fee_rate: feeRate
        });
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleChangeSubmit = async () => {
    try {
      const values = await changeForm.validateFields();
      setChangeLoading(true);
      const payload = {
        target_status: values.target_status,
        reason: values.reason,
        amount: values.amount
      };
      const response = await api.post(`/admin/third-party-withdrawals/${changeRecord.withdraw_no}/status`, payload);
      if (response.code === 200) {
        message.success('代付状态已变更');
        setChangeVisible(false);
        setChangeRecord(null);
        changeForm.resetFields();
        loadData();
      }
    } catch (error) {
      message.error(error.message || '变更失败');
    } finally {
      setChangeLoading(false);
    }
  };

  const getStatusTag = (status, auditStatus) => {
    if (auditStatus === 'approved') {
      return <Tag color="green">已通过</Tag>;
    }
    if (auditStatus === 'rejected') {
      return <Tag color="red">已拒绝</Tag>;
    }
    const statusMap = {
      pending: { color: 'orange', text: '待审核' },
      processing: { color: 'blue', text: '处理中' },
      paid: { color: 'green', text: '通过' },
      failed: { color: 'red', text: '失败' }
    };
    return <Tag color={statusMap[status]?.color || 'default'}>{statusMap[status]?.text || status}</Tag>;
  };

  const renderWithdrawInfo = () => {
    if (!detailData?.withdraw_info) return '-';
    
    const info = typeof detailData.withdraw_info === 'string' 
      ? JSON.parse(detailData.withdraw_info) 
      : detailData.withdraw_info;

    if (!info || Object.keys(info).length === 0) return '-';

    return (
      <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
        <Row gutter={[16, 8]}>
          {Object.entries(info).map(([key, value]) => (
            <Col span={12} key={key}>
              <span style={{ color: '#666' }}>{key}: </span>
              <span style={{ fontWeight: 500 }}>{value}</span>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const columns = [
    {
      title: '代付单号',
      dataIndex: 'withdraw_no',
      key: 'withdraw_no',
      width: 180
    },
    {
      title: '验证短码',
      dataIndex: 'short_code',
      key: 'short_code',
      width: 120,
      render: (code) => code || '-'
    },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 80
    },
    {
      title: '代付金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (val, record) => `${record.currency_code || 'CNY'} ${Number(val || 0).toFixed(2)}`
    },
    // {
    //   title: '人民币金额',
    //   dataIndex: 'cny_amount',
    //   key: 'cny_amount',
    //   width: 120,
    //   render: (val) => val ? `¥${Number(val).toFixed(2)}` : '-'
    // },
    {
      title: '手续费',
      dataIndex: 'fee_amount',
      key: 'fee_amount',
      width: 100,
      render: (val) => val ? Number(val).toFixed(2) : '-'
    },
    {
      title: '代付方式',
      dataIndex: 'withdraw_method',
      key: 'withdraw_method',
      width: 100
    },
    {
      title: '代付信息',
      dataIndex: 'withdraw_info',
      key: 'withdraw_info',
      width: 250,
      render: (info) => {
        if (!info) return '-';
        if (typeof info === 'string') {
          try { info = JSON.parse(info); } catch (e) { return info; }
        }
        const keys = Object.keys(info);
        if (keys.length === 0) return '-';
        return keys.slice(0, 4).map(k => `${k}: ${info[k]}`).join(', ') ;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status, record) => (
        <Space direction="vertical" size={2}>
          {getStatusTag(status, record.audit_status)}
          {record.is_modified == 1 && (
            <Tag color="purple">
              已修改 x{record.modify_count || 1}
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {record.audit_status === 'pending' && (
            <Button type="link" size="small" onClick={() => handleAudit(record)}>
              审核
            </Button>
          )}
          {(record.status === 'paid' || record.status === 'failed' || record.status === 'rejected') && record.audit_status !== 'pending' && (
            <Tooltip title={`变更代付状态（当前：${STATUS_TEXT[record.status] || record.status}）`}>
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleChange(record)} style={{ color: '#722ed1' }}>
                变更
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const renderModalFooter = () => {
    if (modalMode === 'detail') {
      return [
        <Button key="close" onClick={() => setModalVisible(false)}>关闭</Button>
      ];
    }
    return [
      <Button key="cancel" onClick={() => setModalVisible(false)}>取消</Button>,
      <Button key="submit" type="primary" onClick={handleAuditSubmit}>提交审核</Button>
    ];
  };

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline" onFinish={handleSearch}>
          <Form.Item name="app_id" label="应用">
            <Select placeholder="请选择应用" style={{ width: 180 }} allowClear showSearch optionFilterProp="children">
              {appList.map(app => (
                <Option key={app.app_id} value={app.app_id}>{app.app_name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="processing">处理中</Option>
              <Option value="paid">通过</Option>
              <Option value="failed">失败</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="audit_status" label="审核状态">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="keyword" label="关键词">
            <Input placeholder="代付单号" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="dateRange" label="日期">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={handleReset}>重置</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Statistic title="代付总笔数" value={pagination.total} />
          </Col>
          <Col>
            <Statistic title="代付总金额" value={Number(pagination.total_amount || 0)} precision={2} suffix="" />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, limit: pageSize });
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={modalMode === 'audit' ? '审核代付' : '代付详情'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={renderModalFooter()}
        width={800}
      >
        {detailData && (
          <div>
            <Divider orientation="left">基本信息</Divider>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="代付单号">{detailData.withdraw_no}</Descriptions.Item>
              <Descriptions.Item label="验证短码">
                <span style={{ fontWeight: 'bold', color: '#389e0d' }}>{detailData.short_code || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="应用名称">{detailData.app_name}</Descriptions.Item>
              <Descriptions.Item label="商户用户">{detailData.user_real_name} ({detailData.user_phone})</Descriptions.Item>
              <Descriptions.Item label="代付方式">{detailData.withdraw_method}</Descriptions.Item>
              <Descriptions.Item label="代付金额">
                {detailData.currency_code} {Number(detailData.amount || 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="汇率">{detailData.exchange_rate || 1}</Descriptions.Item>
              <Descriptions.Item label="手续费">{Number(detailData.fee_amount || 0).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="实际到账">{Number(detailData.actual_amount || 0).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="回调地址">{detailData.callback_url || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(detailData.status, detailData.audit_status)}</Descriptions.Item>
              <Descriptions.Item label="审核状态">
                {detailData.audit_status === 'approved' ? '已通过' : detailData.audit_status === 'rejected' ? '已拒绝' : '待审核'}
              </Descriptions.Item>
              <Descriptions.Item label="交易号">{detailData.transaction_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {detailData.created_at ? dayjs(detailData.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="审核时间">
                {detailData.audit_time ? dayjs(detailData.audit_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="审核人">{detailData.audit_by_name || '-'}</Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">代付信息</Divider>
            <div>{renderWithdrawInfo()}</div>

            {detailData.audit_status !== 'pending' && (
              <>
                <Divider orientation="left">审核信息</Divider>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="审核备注">{detailData.audit_remark || '-'}</Descriptions.Item>
                </Descriptions>
              </>
            )}

            <Divider orientation="left">审核/变更记录</Divider>
            {historyLoading ? (
              <div style={{ color: '#8c8c8c' }}>加载中...</div>
            ) : historyList.length === 0 ? (
              <div style={{ color: '#8c8c8c' }}>暂无记录</div>
            ) : (
              <Timeline
                items={historyList.map((item) => ({
                  color: item.is_reversal ? 'red' : (item.new_status === 'paid' ? 'green' : 'orange'),
                  children: (
                    <div>
                      <Space size={4} wrap>
                        <span style={{ color: '#8c8c8c', fontSize: '12px' }}>{dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}</span>
                        <Tag color={item.is_modified ? 'purple' : 'blue'}>{item.is_modified ? '变更' : '确认'}</Tag>
                        {item.is_reversal ? <Tag color="red">冲正</Tag> : null}
                      </Space>
                      <div style={{ marginTop: 4 }}>
                        {STATUS_TEXT[item.old_status] || item.old_status || '-'}
                        {' -> '}
                        {STATUS_TEXT[item.new_status] || item.new_status}
                      </div>
                      {item.amount != null && (
                        <div style={{ fontSize: '12px' }}>
                          {item.original_amount != null ? (
                            <>金额: <span style={{ textDecoration: 'line-through', color: '#8c8c8c' }}>{item.original_amount}</span> → <span style={{ fontWeight: 600 }}>{item.amount}</span> {detailData?.currency_code || 'CNY'}</>
                          ) : (
                            <>金额: {item.amount} {detailData?.currency_code || 'CNY'}</>
                          )}
                        </div>
                      )}
                      {item.operator_name && (
                        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>操作人: {item.operator_name}</div>
                      )}
                      {item.remark && (
                        <div style={{ fontSize: '12px', color: '#595959', marginTop: 4 }}>备注: {item.remark}</div>
                      )}
                    </div>
                  ),
                }))}
              />
            )}

            {modalMode === 'audit' && detailData.audit_status === 'pending' && (
              <div style={{ marginTop: 24 }}>
                <Divider orientation="left">审核操作</Divider>
                <Form form={auditForm} layout="vertical"
                  onValuesChange={(changedValues, allValues) => {
                    if (changedValues.amount !== undefined) {
                      const amount = parseFloat(changedValues.amount) || 0;
                      const exchangeRate = parseFloat(allValues.exchange_rate) || 1;
                      const feeRate = parseFloat(allValues.fee_rate) || 0;
                      auditForm.setFieldsValue({
                        cny_amount: amount / exchangeRate,
                        fee_amount: amount * feeRate
                      });
                    }
                  }}
                >
                  <Form.Item label="代付金额">
                    <Input value={`${detailData.currency_code || 'CNY'} ${Number(detailData.amount || 0).toFixed(2)}`} disabled />
                  </Form.Item>
                  <Form.Item
                    label="新代付金额"
                    name="amount"
                    rules={[
                      { required: true, message: '请输入代付金额' },
                      { type: 'number', min: 0.01, message: '金额必须大于0' }
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0.01}
                      step={0.01}
                      precision={2}
                      placeholder="请输入代付金额（不修改则保持原金额）"
                      addonAfter={detailData.currency_code || 'CNY'}
                    />
                  </Form.Item>
                  <Form.Item label="手续费" name="fee_amount">
                    <InputNumber
                      style={{ width: '100%' }}
                      disabled
                      addonAfter={detailData.currency_code || 'CNY'}
                    />
                  </Form.Item>
                  <Form.Item name="audit_status" label="审核结果" rules={[{ required: true, message: '请选择审核结果' }]}>
                    <Select placeholder="请选择审核结果">
                      <Option value="approved">通过</Option>
                      <Option value="rejected">拒绝</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="audit_remark" label="审核备注">
                    <TextArea rows={3} placeholder="请输入审核备注（可选）" />
                  </Form.Item>
                </Form>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="变更代付状态"
        open={changeVisible}
        onCancel={() => {
          setChangeVisible(false);
          setChangeRecord(null);
          changeForm.resetFields();
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        {changeRecord && (
          <Form
            form={changeForm}
            layout="vertical"
            onFinish={handleChangeSubmit}
            onValuesChange={(changedValues, allValues) => {
              if (changedValues.amount !== undefined) {
                const amount = parseFloat(changedValues.amount) || 0;
                const exchangeRate = parseFloat(allValues.exchange_rate) || 1;
                const feeRate = parseFloat(allValues.fee_rate) || 0;
                changeForm.setFieldsValue({
                  cny_amount: amount / exchangeRate,
                  fee_amount: amount * feeRate
                });
              }
            }}
          >
            {changeTargetStatus === 'failed' && (
              <Form.Item>
                <Alert
                  type="warning"
                  showIcon
                  message="该操作将恢复商户余额"
                  description="代付状态变更为失败后，将恢复商户被扣减的余额。"
                />
              </Form.Item>
            )}
            {changeTargetStatus === 'paid' && (
              <Form.Item>
                <Alert
                  type="info"
                  showIcon
                  message="重新确认为通过"
                  description="确认后代付状态将变更为通过，并扣减商户余额。"
                />
              </Form.Item>
            )}
            <Form.Item label="代付单号">
              <Input value={changeRecord.withdraw_no} disabled />
            </Form.Item>
            <Form.Item label="应用名称">
              <Input value={changeRecord.app_name} disabled />
            </Form.Item>
            <Form.Item label="当前状态">
              <Input value={STATUS_TEXT[changeRecord.status] || changeRecord.status} disabled />
            </Form.Item>
            <Form.Item label="代付金额">
              <Input value={`${changeRecord.currency_code || 'CNY'} ${Number(changeRecord.amount || 0).toFixed(2)}`} disabled />
            </Form.Item>
            <Form.Item
              label="目标状态"
              name="target_status"
              rules={[{ required: true, message: '请选择目标状态' }]}
            >
              <Select placeholder="请选择目标状态">
                <Option value="paid">通过</Option>
                <Option value="failed">失败</Option>
              </Select>
            </Form.Item>
            {changeTargetStatus === 'paid' && (
              <>
                <Form.Item
                  label="代付金额"
                  name="amount"
                  rules={[
                    { required: true, message: '请输入代付金额' },
                    { type: 'number', min: 0.01, message: '金额必须大于0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0.01}
                    step={0.01}
                    precision={2}
                    placeholder="请输入代付金额"
                    addonAfter={changeRecord.currency_code || 'CNY'}
                  />
                </Form.Item>
                <Form.Item label="手续费" name="fee_amount">
                  <InputNumber
                    style={{ width: '100%' }}
                    disabled
                    addonAfter={changeRecord.currency_code || 'CNY'}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item
              label="变更备注"
              name="reason"
              rules={[{ required: true, message: '请输入变更备注' }]}
            >
              <TextArea rows={3} placeholder="请输入变更备注" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={changeLoading}>
                  确认变更
                </Button>
                <Button onClick={() => setChangeVisible(false)}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default ThirdPartyWithdrawals;
